import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { LayoutDashboard, Users, Truck, ShieldAlert, BarChart, Map, Mail, Settings, UserCheck, Activity, UserPlus, FileWarning, Briefcase, Coins, ArrowUp, ArrowDown, PieChart as PieChartIcon, TrendingUp, Loader2 } from 'lucide-react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { ResponsiveContainer, PieChart, Pie, Cell, BarChart as RechartsBarChart, XAxis, YAxis, Tooltip, Legend, Bar } from 'recharts';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useTheme } from 'next-themes';
import { adminSidebarLinks } from '@/config/adminSidebarLinks';
import AdvancedChart from '@/components/charts/AdvancedChart';
import Divider from '@/components/ui/divider';
import { adminAPI } from '@/services/api';
import { toast } from '@/components/ui/use-toast';

const StatCard = ({ title, value, change, changeType, children }) => (
    <div className="bg-card border border-border rounded-xl p-6 hover:border-secondary transition-colors w-64 flex-shrink-0 lg:w-auto">
        <div className="flex justify-between items-start mb-2 min-h-[20px]">
            <p className="text-sm text-muted-foreground flex-1">{title}</p>
            <div className="flex-shrink-0">{children}</div>
        </div>
        <p className="text-3xl lg:text-4xl font-bold text-foreground mb-1">{value}</p>
        {change && (
            <div className={cn("flex items-center text-sm", changeType === 'positive' ? 'text-green-500' : 'text-red-500')}>
                {changeType === 'positive' ? <ArrowUp className="w-4 h-4 mr-1" /> : <ArrowDown className="w-4 h-4 mr-1" />}
                <span>{change}</span>
            </div>
        )}
    </div>
);

const DetailStatCard = ({ title, value, subValue1, subValue2, children }) => (
    <div className="bg-card border border-border rounded-xl p-6 flex flex-col justify-between hover:border-secondary transition-colors w-64 flex-shrink-0 lg:w-auto">
        <div>
            <div className="flex justify-between items-start mb-2 min-h-[20px]">
                <p className="text-sm text-muted-foreground flex-1">{title}</p>
                <div className="flex-shrink-0">{children}</div>
            </div>
            <p className="text-3xl font-bold text-foreground mb-2">{value}</p>
        </div>
        <div>
            <p className="text-sm text-foreground/80">{subValue1}</p>
            <p className="text-sm text-muted-foreground">{subValue2}</p>
        </div>
    </div>
);


const AdminDashboard = () => {
    const { theme } = useTheme();
    const [selectedPeriod, setSelectedPeriod] = useState('all');
    const [loading, setLoading] = useState(true);
    const [statistics, setStatistics] = useState(null);
    const [chartData, setChartData] = useState([]);
    const [recentActivities, setRecentActivities] = useState([]);

    // Charger les données au montage et quand la période change
    useEffect(() => {
        loadDashboardData();
    }, [selectedPeriod]);

    const loadDashboardData = async () => {
        try {
            setLoading(true);
            const [statsRes, chartRes, activitiesRes] = await Promise.all([
                adminAPI.getStatistics(),
                adminAPI.getChartData(selectedPeriod),
                adminAPI.getRecentActivities(10),
            ]);

            setStatistics(statsRes.data);
            setChartData(chartRes.data);
            setRecentActivities(activitiesRes.data);
        } catch (error) {
            console.error('Erreur lors du chargement des données du dashboard:', error);
            toast({
                title: 'Erreur',
                description: 'Impossible de charger les données du dashboard.',
                variant: 'destructive',
            });
        } finally {
            setLoading(false);
        }
    };

    // Configuration des séries pour le graphique avancé
    const chartSeries = [
        { key: 'utilisateurs', name: 'Utilisateurs', color: '#3b82f6', type: 'line' },
        { key: 'missions', name: 'Missions', color: '#10b981', type: 'bar' },
        { key: 'revenus', name: 'Revenus (M GNF)', color: '#f59e0b', type: 'line' },
    ];

    // Callback pour gérer le changement de période
    const handlePeriodChange = (period) => {
        setSelectedPeriod(period);
    };

    // Préparer les données pour les graphiques
    const missionStatusData = statistics?.mission_status ? [
        { name: 'Terminées', value: statistics.mission_status.completed || 0 },
        { name: 'En cours', value: statistics.mission_status.in_progress || 0 },
        { name: 'Annulées', value: statistics.mission_status.cancelled || 0 },
    ] : [];
    const PIE_COLORS = ['#10b981', '#3b82f6', '#ef4444'];

    const userRolesData = statistics?.user_roles ? [
        { name: 'Clients', value: statistics.user_roles.clients || 0 },
        { name: 'Transporteurs', value: statistics.user_roles.transporters || 0 },
    ] : [];

    // Fonction pour formater les montants
    const formatAmount = (amount) => {
        if (amount >= 1000000) {
            return `${(amount / 1000000).toFixed(1)}M GNF`;
        } else if (amount >= 1000) {
            return `${(amount / 1000).toFixed(0)}K GNF`;
        }
        return `${amount.toFixed(0)} GNF`;
    };

    // Fonction pour obtenir l'icône selon le type d'activité
    const getActivityIcon = (type) => {
        switch (type) {
            case 'validation':
                return UserCheck;
            case 'dispute':
                return FileWarning;
            case 'user':
                return UserPlus;
            default:
                return Activity;
        }
    };

    const tooltipStyle = {
        backgroundColor: theme === 'dark' ? '#021f17' : 'hsl(var(--background))',
        border: '1px solid hsl(var(--border))',
        color: 'hsl(var(--foreground))'
    };

    if (loading && !statistics) {
        return (
            <>
                <Helmet>
                    <title>Tableau de bord Admin - LogiConnect</title>
                </Helmet>
                <DashboardLayout sidebarLinks={adminSidebarLinks}>
                    <div className="flex items-center justify-center min-h-[400px]">
                        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                    </div>
                </DashboardLayout>
            </>
        );
    }

    const kpis = statistics?.kpis || {};
    const detailedMetrics = statistics?.detailed_metrics || {};

    return (
        <>
            <Helmet>
                <title>Tableau de bord Admin - LogiConnect</title>
            </Helmet>
            <DashboardLayout sidebarLinks={adminSidebarLinks}>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-8">Tableau de bord Administrateur</h1>

                <div className="mb-8">
                    <h2 className="text-lg font-semibold text-muted-foreground mb-4">KPIs Principaux</h2>
                    <div className="lg:grid lg:grid-cols-4 lg:gap-6">
                        <div className="flex space-x-6 overflow-x-auto pb-4 -mx-4 px-4 lg:contents" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                            <StatCard
                                title="CA Total"
                                value={formatAmount(kpis.total_revenue || 0)}
                                change={kpis.revenue_change_percent ? `${kpis.revenue_change_percent >= 0 ? '+' : ''}${kpis.revenue_change_percent.toFixed(1)}% vs mois` : null}
                                changeType={kpis.revenue_change_percent >= 0 ? 'positive' : 'negative'}>
                                <Coins className="w-5 h-5 text-green-500"/>
                            </StatCard>
                            <StatCard
                                title="Commission"
                                value={formatAmount(kpis.total_commission || 0)}
                                change={kpis.commission_change_percent ? `${kpis.commission_change_percent >= 0 ? '+' : ''}${kpis.commission_change_percent.toFixed(1)}% vs mois` : null}
                                changeType={kpis.commission_change_percent >= 0 ? 'positive' : 'negative'}>
                                <Coins className="w-5 h-5 text-green-500"/>
                            </StatCard>
                            <StatCard
                                title="Utilisateurs"
                                value={kpis.total_users || 0}
                                change={kpis.users_change_percent ? `${kpis.users_change_percent >= 0 ? '+' : ''}${kpis.users_change_percent.toFixed(1)}% vs mois` : null}
                                changeType={kpis.users_change_percent >= 0 ? 'positive' : 'negative'}>
                                <Users className="w-5 h-5 text-blue-500"/>
                            </StatCard>
                            <StatCard
                                title="Litiges"
                                value={kpis.open_disputes || 0}
                                change={kpis.disputes_change !== undefined ? `${kpis.disputes_change >= 0 ? '+' : ''}${kpis.disputes_change} vs hier` : null}
                                changeType={kpis.disputes_change <= 0 ? 'positive' : 'negative'}>
                                <ShieldAlert className="w-5 h-5 text-red-500"/>
                            </StatCard>
                        </div>
                    </div>
                </div>

                {/* Divider élégant entre KPIs et Métriques */}
                <Divider variant="gradient" icon={TrendingUp} label="Métriques détaillées" />

                <div className="mb-8">
                    <div className="lg:grid lg:grid-cols-4 lg:gap-6">
                        <div className="flex space-x-6 overflow-x-auto pb-4 -mx-4 px-4 lg:contents" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                            <DetailStatCard
                                title="Transporteurs"
                                value={detailedMetrics.transporters?.total || 0}
                                subValue1={`${detailedMetrics.transporters?.new_this_month || 0} nouveaux`}
                                subValue2={`${detailedMetrics.transporters?.pending_validation || 0} à vérifier`}>
                               <Truck className="w-5 h-5 text-blue-500"/>
                            </DetailStatCard>
                            <DetailStatCard
                                title="Clients"
                                value={detailedMetrics.clients?.total || 0}
                                subValue1={`${detailedMetrics.clients?.new_this_month || 0} nouveaux`}
                                subValue2={`${detailedMetrics.clients?.enterprises || 0} entreprises`}>
                               <Users className="w-5 h-5 text-purple-500"/>
                            </DetailStatCard>
                            <DetailStatCard
                                title="Véhicules"
                                value={detailedMetrics.vehicles?.total || 0}
                                subValue1={`${detailedMetrics.vehicles?.in_maintenance || 0} en maintenance`}
                                subValue2={`${detailedMetrics.vehicles?.active || 0} actifs`}>
                               <Truck className="w-5 h-5 text-yellow-500"/>
                            </DetailStatCard>
                            <DetailStatCard
                                title="Taux Conversion"
                                value={`${detailedMetrics.conversion_rate?.rate || 0}%`}
                                subValue1={detailedMetrics.conversion_rate?.change ? `${detailedMetrics.conversion_rate.change >= 0 ? '+' : ''}${detailedMetrics.conversion_rate.change.toFixed(1)}% vs mois` : null}
                                subValue2="Demandes → Missions">
                               <BarChart className="w-5 h-5 text-green-500"/>
                            </DetailStatCard>
                        </div>
                    </div>
                </div>


                <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mb-6">
                    <div className="lg:col-span-3">
                        <AdvancedChart
                            data={chartData}
                            series={chartSeries}
                            title="Aperçu de la croissance"
                            defaultPeriod={selectedPeriod}
                            defaultChartType="composed"
                            onPeriodChange={handlePeriodChange}
                            chartConfig={{ height: 350 }}
                        />
                    </div>
                    <div className="lg:col-span-2 bg-card border border-border rounded-xl">
                        <div className="p-6 border-b border-border">
                            <h2 className="text-xl font-semibold text-foreground">Actions rapides</h2>
                        </div>
                        <div className="p-6 space-y-4">
                            {recentActivities.length > 0 ? (
                                recentActivities.map((activity, index) => {
                                    const ActivityIcon = getActivityIcon(activity.type);
                                    return (
                                        <div key={index} className="flex items-start space-x-4">
                                            <div className="w-10 h-10 flex-shrink-0 bg-accent rounded-full flex items-center justify-center">
                                                <ActivityIcon className="w-5 h-5 text-muted-foreground"/>
                                            </div>
                                            <div className="flex-1">
                                                <p className="text-sm text-foreground">{activity.text}</p>
                                                <p className="text-xs text-muted-foreground">{activity.time}</p>
                                            </div>
                                            <Button asChild variant="ghost" size="sm" className="text-secondary hover:text-secondary hover:bg-secondary/10">
                                                <Link to={activity.link}>Voir</Link>
                                            </Button>
                                        </div>
                                    );
                                })
                            ) : (
                                <p className="text-sm text-muted-foreground text-center py-4">Aucune activité récente</p>
                            )}
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-card border border-border rounded-xl">
                        <div className="p-6 border-b border-border flex justify-between items-center">
                            <h2 className="text-xl font-semibold text-foreground">Statut des Missions</h2>
                            <PieChartIcon className="w-5 h-5 text-muted-foreground" />
                        </div>
                        <div className="p-6 h-80">
                            {missionStatusData.length > 0 ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie data={missionStatusData} cx="50%" cy="50%" labelLine={false} outerRadius={100} fill="#8884d8" dataKey="value" nameKey="name" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                                            {missionStatusData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip contentStyle={tooltipStyle}/>
                                        <Legend wrapperStyle={{fontSize: "12px"}}/>
                                    </PieChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="flex items-center justify-center h-full">
                                    <p className="text-sm text-muted-foreground">Aucune donnée disponible</p>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="bg-card border border-border rounded-xl">
                        <div className="p-6 border-b border-border flex justify-between items-center">
                            <h2 className="text-xl font-semibold text-foreground">Répartition des Rôles</h2>
                            <Users className="w-5 h-5 text-muted-foreground" />
                        </div>
                        <div className="p-6 h-80">
                            {userRolesData.length > 0 ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <RechartsBarChart data={userRolesData}>
                                        <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
                                        <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
                                        <Tooltip contentStyle={tooltipStyle}/>
                                        <Bar dataKey="value" barSize={40}>
                                            <Cell fill="rgba(96, 165, 250, 0.6)" />
                                            <Cell fill="rgba(192, 132, 252, 0.6)" />
                                        </Bar>
                                    </RechartsBarChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="flex items-center justify-center h-full">
                                    <p className="text-sm text-muted-foreground">Aucune donnée disponible</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </DashboardLayout>
        </>
    );
};

export default AdminDashboard;
